import { CloseOutlined } from "@ant-design/icons";
import type { SelectProps as AntdSelectProps, RefSelectProps } from "antd";
import { Select as AntdSelect } from "antd";
import { DefaultOptionType } from "antd/es/select";
import React, { forwardRef, useEffect, useMemo, useState } from "react";

export interface SelectProps extends AntdSelectProps {
  ariaDescribedby?: string;
  ariaLabel?: string;
  selectAll?: boolean;
  selectAllLabel?: string;
}

const Select = forwardRef<RefSelectProps, SelectProps>(
  (
    {
      ariaDescribedby,
      ariaLabel,
      onChange,
      options,
      optionFilterProp = "label",
      placeholder = "Select...",
      selectAll,
      selectAllLabel = "All",
      tagRender,
      value,
      ...rest
    },
    ref
  ) => {
    const allOptionValues = useMemo(
      () => options?.map((option) => option.value) ?? [],
      [options]
    );
    const allOptions = useMemo(() => {
      if (selectAll && options) {
        return [{ label: selectAllLabel, value: "all" }, ...options];
      } else {
        return options;
      }
    }, [options, selectAll, selectAllLabel]);

    // Internal state for selected values
    const [selected, setSelected] = useState(() => {
      if (selectAll && value.length === allOptionValues.length) {
        return ["all", ...value];
      } else {
        return value;
      }
    });

    // Collapse tags to a single "All" tag when everything is selected.
    // Closing this tag clears the selection.
    const allTagRender =
      selectAll &&
      selected?.includes("all") &&
      (rest.mode === "multiple" || rest.mode === "tags")
        ? (tagProps: any) => {
            // Render only once for the first selected item
            const firstValue = selected[0];
            if (tagProps.value !== firstValue) return <></>;

            const onCloseAll = () => {
              onChange?.([], []);
              setSelected([]);
            };

            return (
              <span className="ant-select-selection-item">
                <span className="ant-select-selection-item-content">
                  {selectAllLabel}
                </span>
                {tagProps.closable !== false && (
                  <span
                    className="ant-select-selection-item-remove"
                    onClick={onCloseAll}
                  >
                    <CloseOutlined />
                  </span>
                )}
              </span>
            );
          }
        : undefined;

    const handleOnChange = (
      value: any,
      option: DefaultOptionType | DefaultOptionType[]
    ) => {
      if (onChange) {
        if (selectAll) {
          if (value.includes("all")) {
            // if "all" is already selected, that means we're deselecting something
            // else "all" is being selected, so select all options
            if (selected?.includes("all")) {
              const newValue = value.filter((v: any) => v !== "all");
              onChange(newValue, option);
              setSelected(newValue);
            } else {
              onChange(allOptionValues, allOptions ?? option);
              setSelected(["all", ...allOptionValues]);
            }
          } else if (value.length === allOptionValues.length) {
            // if "all" was selected but not in value now, clear all selection
            // else all options are now selected so include "all"
            if (selected?.includes("all")) {
              onChange([], []);
              setSelected([]);
            } else {
              onChange(value, option);
              setSelected(["all", ...value]);
            }
          } else {
            onChange(value, option);
            setSelected(value);
          }
        } else {
          onChange(value, option);
          setSelected(value);
        }
      }
    };

    // This is needed for when value is updated programmatically at the parent level
    useEffect(() => {
      // If selectAll is enabled and value represents all options, include "all"
      if (selectAll && value.length === allOptionValues.length) {
        setSelected(["all", ...value]);
      } else {
        setSelected(value);
      }
    }, [value, allOptionValues, selectAll]);

    return (
      <AntdSelect
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedby}
        onChange={handleOnChange}
        options={allOptions}
        optionFilterProp={optionFilterProp}
        placeholder={placeholder}
        ref={ref}
        style={{ width: "100%", ...(rest.style || {}) }}
        tagRender={allTagRender ?? tagRender}
        value={selected}
        {...rest}
      />
    );
  }
);

Select.displayName = "Select";

export default Select;
